import { boot, show as intercomShow, showMessages } from "@intercom/messenger-js-sdk";

const INTERCOM_APP_ID = "dcw7ecc4";

export type IntercomMessengerUser = {
  id?: string;
  email: string;
  name?: string;
};

const getIntercomFn = () => {
  if (typeof window === "undefined") return undefined;
  return (window as Window & { Intercom?: (cmd: string, data?: unknown) => void })
    .Intercom;
};

const tryIntercomCommand = (command: string): boolean => {
  const intercom = getIntercomFn();
  if (typeof intercom !== "function") return false;
  try {
    intercom(command);
    return true;
  } catch {
    return false;
  }
};

const tryIntercomCommandWithData = (command: string, data: unknown): boolean => {
  const intercom = getIntercomFn();
  if (typeof intercom !== "function") return false;
  try {
    intercom(command, data);
    return true;
  } catch {
    return false;
  }
};

const tryOpenMessenger = (): boolean => {
  if (tryIntercomCommand("show")) return true;
  if (tryIntercomCommand("showMessages")) return true;
  if (tryIntercomCommand("showNewMessage")) return true;
  try {
    showMessages();
    return true;
  } catch {
    /* */
  }
  try {
    intercomShow();
    return true;
  } catch {
    return false;
  }
};

/** Close/minimize Intercom messenger if it's open. */
export function closeIntercomMessenger(): void {
  // Try classic widget commands first
  if (tryIntercomCommand("hide")) return;
  if (tryIntercomCommand("hideMessages")) return;

  // Some setups accept update to keep launcher hidden; doesn't fully close
  tryIntercomCommandWithData("update", { hide_default_launcher: true });
}

/** Open Intercom messenger (native header; retries until SDK is ready). */
export function openIntercomMessenger(user?: IntercomMessengerUser): void {
  const bootUser = () => {
    if (!user?.email) return;
    try {
      boot({
        app_id: INTERCOM_APP_ID,
        user_id: user.id || user.email,
        email: user.email,
        name: user.name,
      });
    } catch {
      /* IntercomWidget may have booted already */
    }
  };

  const attempt = (n: number) => {
    if (tryOpenMessenger()) return;
    if (n === 0) bootUser();
    if (n < 15) setTimeout(() => attempt(n + 1), 250);
  };

  if (typeof window !== "undefined") {
    window.addEventListener(
      "bilenia-intercom-ready",
      () => attempt(0),
      { once: true },
    );
  }

  attempt(0);
}

export { INTERCOM_APP_ID };
